import { ChangeDetectionStrategy, Component, DestroyRef, inject } from '@angular/core';
import { API, PlayerHardwareDTO } from '../../../services/api/api';
import { UtilService } from 'src/app/services/util.service';
import { ModalService } from '../../../services/gui/modal/modal.service';
import { TranslateService } from '@ngx-translate/core';
import { ErrorService } from 'src/app/services/error.service';
import { ToasterService, ToastType } from 'src/app/services/toaster/toaster.service';
import { PermissionService } from '../../../services/permission.service';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { FilterPanelConfig } from '@ngx-seatback/layout';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';
import { SbSplitButton } from '../../../services/global/global-interfaces';
import { RoutingID } from '../../../routes/sb-routing-enum';
import { RequestClass } from '../../../services/global/RequestClass';

type SplitButtonAction = 'details' | 'edit' | 'delete';

@Component({
  selector: 'sb-page-device-hardware',
  templateUrl: './player-hardware.component.html',
  styleUrls: ['./player-hardware.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlayerHardwareComponent extends RequestClass {
  public readonly util = inject(UtilService);
  public readonly modalService = inject(ModalService);
  public readonly translate = inject(TranslateService);
  public readonly permissionService = inject(PermissionService);
  private readonly toastHandler = inject(ToasterService);
  private readonly errorHandler = inject(ErrorService);
  private readonly api = inject(API);
  private readonly stringTransformation = inject(StringTransformationService);
  private readonly destroyRef = inject(DestroyRef);

  // table wrapper variables
  playerHardware: PlayerHardwareDTO[] = [];

  // filter variables
  exactMatch: SearchOptions = 'contains';
  search: string | null = null;
  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    hideTagSection: true,
    button: { visibleWithoutChanges: true },
  };

  refreshFromServer() {
    if (!this.pagination) {
      this.loading.set(false);
      return;
    }
    const sub = this.api
      .ca_md_PlayerHardware_retrieveAll(
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
        },
        this.pagination
      )
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (player: PlayerHardwareDTO[]) => {
          this.playerHardware = this.buildSplitButtons(player);
        },
      });
    this.subs.push(sub);
  }

  deletePlayer(playerId: number) {
    if (!playerId || playerId < 0) {
      throw new Error('Missing player id');
    }
    // show confirm modal
    this.modalService
      .confirm({
        title: this.translate.instant('lang.globals.approve'),
        message: this.translate.instant('lang.devices.deleteDeviceHardwareMessage'),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (result) {
          this.api
            .ca_md_PlayerHardware_deletePlayerHardware(playerId)
            .pipe(take(1))
            .subscribe({
              next: () => {
                this.toastHandler.toast(ToastType.SUCCESS, this.translate.instant('lang.devices.deletedDeviceHardware'));

                // refresh table after deletion
                this.refreshFromServer();
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        }
      })
      .catch(error => {
        this.errorHandler.errorHandler(error);
      });
  }

  buildSplitButtons<T extends { id: any; global: boolean }>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      if (this.permissionService.hasPermissions([this.permissionService.Permission.player_hardware_show])) {
        item.sbButtons.push({
          icon: 'details',
          tooltip: 'lang.globals.details',
          link: RoutingID.playerHardwareOverview,
          pathParams: [{ id: 'playerHardwareID', value: item?.id }],
        });
      }
      if (!item.global) {
        if (this.permissionService.hasPermissions([this.permissionService.Permission.player_hardware_edit])) {
          item.sbButtons.push({
            icon: 'edit',
            tooltip: 'lang.globals.edit',
            link: RoutingID.deviceHardwareEdit,
            queryParams: { returnTo: this.util.getPageLink() },
            pathParams: [{ id: 'playerHardwareID', value: item?.id }],
          });
        }
        if (this.permissionService.hasPermissions([this.permissionService.Permission.player_hardware_delete])) {
          item.sbButtons.push({
            icon: 'trash',
            iconClass: 'hover:!tw-bg-sbRed-500 *:tw-text-sbRed-500',
            action: 'delete',
            tooltip: 'lang.globals.delete',
          });
        }
      }
    });
    return result;
  }

  resolveSplitButtonAction(player: PlayerHardwareDTO, $event: SplitButtonAction) {
    switch ($event) {
      case 'delete':
        this.deletePlayer(player?.id);
        break;
      default:
        console.error('Missing action implementation of Splitbutton');
        break;
    }
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
  }
}
